import { DateTime } from 'luxon';
import { commaSeparatedSymbols } from '@benzinga/utils';
import { DividendsDateSort, DividendsQueryParams } from './entities';

export type DividendsQueryParamsInput = Omit<
  Partial<DividendsQueryParams>,
  'date' | 'dateFrom' | 'dateTo' | 'symbols'
> & {
  date?: Date | string;
  dateFrom?: Date | string;
  dateTo?: Date | string;
  symbols?: string[] | string;
};

const DIVIDENDS_DATE_SORT_FIELDS = new Set<DividendsDateSort>(['announced', 'ex', 'payable', 'record']);

const formatQueryDate = (value: Date | string): string =>
  value instanceof Date ? DateTime.fromJSDate(value).toFormat('yyyy-MM-dd') : String(value);

const toSymbolList = (symbols: string[] | string | undefined): string[] | undefined => {
  if (Array.isArray(symbols)) {
    const list = symbols.map(symbol => symbol.trim()).filter(Boolean);
    return list.length ? list : undefined;
  }

  const tickers = commaSeparatedSymbols(symbols);
  if (!tickers) return undefined;

  const list = tickers
    .split(',')
    .map(symbol => symbol.trim())
    .filter(Boolean);
  return list.length ? list : undefined;
};

export const normalizeDividendsQueryParams = (params: DividendsQueryParamsInput = {}): DividendsQueryParams => {
  const dateSort = params.dateSort && DIVIDENDS_DATE_SORT_FIELDS.has(params.dateSort) ? params.dateSort : undefined;
  const symbols = toSymbolList(params.symbols);

  return {
    ...(params.date && { date: formatQueryDate(params.date) }),
    ...(params.dateFrom && { dateFrom: formatQueryDate(params.dateFrom) }),
    ...(params.dateTo && { dateTo: formatQueryDate(params.dateTo) }),
    ...(dateSort && { dateSort }),
    ...(params.fields && { fields: params.fields }),
    ...(params.id && { id: params.id }),
    ...(params.importance !== undefined && { importance: params.importance }),
    ...(params.page !== undefined && { page: params.page }),
    pageSize: params.pageSize ?? 1000,
    ...(symbols && { symbols }),
    ...(params.updated !== undefined && { updated: params.updated }),
    ...(params.display && { display: params.display }),
    ...(params.dividendYield !== undefined && { dividendYield: params.dividendYield }),
    ...(params.dividendYieldOperation && { dividendYieldOperation: params.dividendYieldOperation }),
  };
};
